import { Constants } from '@/data/constants';
import { Region } from '@/enums/region';
import { iconLibrary } from '@/shared/icons';
import { timeState } from '@/shared/state/time.svelte';
import { DbResetType } from '@/shared/stores/db/enums';
import { getNextWeeklyResetFromTime } from '@/utils/get-next-reset';
import type { Character } from '@/types';
import type { Chore, Task } from '@/types/tasks';

const bountifulQuestIds = [
    91182, // Parhelion Plaza
    91183, // Sunkiller Sanctum
    91184, // Shadowguard Point
    91185, // The Grudge Pit
    91186, // Collegiate Calamity
    91187, // The Gulf of Memory
    91188, // Atal'Aman
    91189, // The Shadow Enclave
    91190, // Twilight Crypts
    92444, // The Darkway
    95715, // Gnarldor Isle
    95716, // Ring of Glory
];

export const bountifulFunc = (index: number) => {
    return (char: Character) => {
        const completedQuestIds = bountifulQuestIds.filter((questId) =>
            char.quests?.hasQuestById?.has(questId)
        );
        completedQuestIds.sort();

        return completedQuestIds[index] ? [completedQuestIds[index]] : [];
    };
};

export const midDelves: Task = {
    key: 'midDelves',
    name: '[Mid] Delves',
    shortName: 'Delve',
    minimumLevel: 80,
    showSeparate: true,
    sumChores: true,
    chores: [
        {
            key: 'arcaneRemnant',
            name: '{item:262586}', // Primeval Arcane Remnant
            icon: iconLibrary.gameUnstableOrb,
            minimumLevel: 80,
            accountWide: true,
            alwaysStarted: true,
            questReset: DbResetType.Weekly,
            questIds: [93784],
        },
        {
            key: 'repAmani',
            name: 'Rep: {faction:2696}',
            minimumLevel: 90,
            accountWide: true,
            alwaysStarted: true,
            questReset: DbResetType.Weekly,
            questIds: [93819],
        },
        {
            key: 'repHarati',
            name: 'Rep: {faction:2704}',
            minimumLevel: 90,
            accountWide: true,
            alwaysStarted: true,
            questReset: DbResetType.Weekly,
            questIds: [93822],
        },
        {
            key: 'repSilvermoon',
            name: 'Rep: {faction:2710}',
            minimumLevel: 90,
            accountWide: true,
            alwaysStarted: true,
            questReset: DbResetType.Weekly,
            questIds: [93821],
        },
        {
            key: 'repSingularity',
            name: 'Rep: {faction:2699}',
            minimumLevel: 90,
            accountWide: true,
            alwaysStarted: true,
            questReset: DbResetType.Weekly,
            questIds: [93820],
        },
        {
            key: 'repZuljarra',
            name: 'Rep: {faction:2772}',
            minimumLevel: 90,
            accountWide: true,
            alwaysStarted: true,
            questReset: DbResetType.Weekly,
            questIds: [99222],
        },
        null,
        {
            key: 'bounty',
            name: `{item:${Constants.items.trovehuntersBounty}}`, // Trovehunter's Bounty [Season 2]
            icon: iconLibrary.gameTreasureMap,
            minimumLevel: 90,
            alwaysStarted: true,
            questReset: DbResetType.Weekly,
            questResetForced: true,
            questIds: [86371],
        },
        {
            key: 'nullaeus',
            name: 'Boss Invasion',
            minimumLevel: 90,
            alwaysStarted: true,
            questIds: [92887],
            questReset: DbResetType.Weekly,
        },
        {
            key: 'abundant',
            name: 'Abundantly Bountiful',
            icon: iconLibrary.gameGoldStack,
            minimumLevel: 90,
            questReset: DbResetType.Weekly,
            questResetForced: true,
            questIds: [97064],
        },
        {
            key: 'bountiful',
            name: 'Bountiful Delves',
            icon: iconLibrary.gameKey,
            minimumLevel: 90,
            questReset: DbResetType.Daily,
            questResetForced: true,
            alwaysStarted: true,
            subChores: [0, 1, 2, 3].map((n) => ({
                key: `bountiful${n}`,
                name: `Bountiful ${n + 1}`,
                alwaysStarted: true,
                questIds: bountifulFunc(n),
            })),
        },
        {
            key: 'gilded',
            name: 'Gilded Stash',
            icon: iconLibrary.gameCutDiamond,
            minimumLevel: 90,
            questReset: DbResetType.Weekly,
            questResetForced: true,
            showQuestName: true,
            subChores: [
                {
                    key: 'stashes',
                    name: '{currency:3290}',
                    alwaysStarted: true,
                    progressFunc: (char) => {
                        const expiresAt = getNextWeeklyResetFromTime(
                            char.lastSeenAddon || timeState.slowTime,
                            char.realm?.region || Region.US,
                            char
                        );
                        const have =
                            expiresAt < timeState.slowTime ? 0 : char.weekly?.delveGilded || 0;
                        return { have, need: 4 };
                    },
                },
            ],
        },
    ],
};
